import { capitalize } from 'lodash'
import { useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Item } from '../../models/Item'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import { Checkbox } from '../common/fields/Checkbox'
import { CurrencyField } from '../common/fields/CurrencyField'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { SelectedPhoto } from '../workflows/PhotoPicker'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    item: Item
    onClose: () => void
    onDelete?: () => void
    isLoading?: boolean
    enableAllFields?: boolean
    onSave: (item: Item) => void
}

export const ProductPage = ({
    item,
    onClose,
    onDelete,
    isLoading,
    onSave,
    enableAllFields = true,
}: Props) => {
    const [scratchProduct, setScratchProduct] = useState<Item>(item)
    const [selectedPhoto, setSelectedPhoto] = useState<SelectedPhoto | null>(
        item.image?.uri ? { type: 'url', url: item.image.url ?? '' } : null
    )

    const isNextEnabled = useMemo(() => {
        return scratchProduct.name !== '' && scratchProduct.rate !== undefined
    }, [scratchProduct.name, scratchProduct.rate])

    return (
        <WorkflowPage
            title={capitalize(item.type ?? '')}
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        onDelete !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {onDelete !== undefined && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            onClick={onDelete}
                            isLoading={isLoading}
                        >
                            Delete product
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={() => onSave(scratchProduct)}
                        disabled={isLoading || !isNextEnabled}
                        isLoading={isLoading}
                    >
                        Save
                    </Button>
                </div>
            }
            onClose={onClose}
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    {enableAllFields && (
                        <WorkflowPage.PhotoPicker
                            onSelectPhoto={(photoFile) => {
                                setSelectedPhoto({ type: 'file', file: photoFile })
                            }}
                            onClearPhoto={() => setSelectedPhoto(null)}
                            selectedPhoto={selectedPhoto}
                        />
                    )}
                    <WorkflowPage.IconSection
                        title="Name (required)"
                        icon={<Icon icon="package.large" background="round" />}
                    >
                        <TextField
                            isFullWidth
                            size="medium"
                            placeholder="Enter name"
                            value={scratchProduct?.name}
                            onClear={() => setScratchProduct((p) => p?.clone({ name: '' }))}
                            onChange={(e) => {
                                setScratchProduct((p) => p?.clone({ name: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Price (required)"
                        icon={<Icon icon="dollar.large" background="round" />}
                    >
                        <CurrencyField
                            isFullWidth
                            size="medium"
                            placeholder="$0.00"
                            value={scratchProduct?.rate ?? 0}
                            icon={'dollar9.large'}
                            onClear={() => {
                                setScratchProduct((p) => p?.clone({ rate: undefined }))
                            }}
                            onChange={(e) => {
                                setScratchProduct((p) => p?.clone({ rate: e }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Description"
                        icon={<Icon icon="note.large" background="round" />}
                    >
                        <TextArea
                            size="medium"
                            placeholder="Enter description"
                            value={scratchProduct?.memo}
                            isAutoResizing
                            onClear={() => setScratchProduct((p) => p?.clone({ memo: '' }))}
                            onChange={(e) => {
                                setScratchProduct((p) => p?.clone({ memo: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Show online"
                        description="Product will appear the clients after booking you online"
                        icon={<Icon icon="computerDollar.large" background="round" />}
                        hasHoverEffect
                        secondaryView={
                            <Checkbox
                                isChecked={scratchProduct?.isPublic}
                                onChange={(e) => {
                                    setScratchProduct((p) => p?.clone({ is_public: e }))
                                }}
                            />
                        }
                    />
                    <WorkflowPage.IconSection
                        title="Track inventory"
                        description="Products sold will automtically deduce from your inventory"
                        icon={<Icon icon="box.large" background="round" />}
                        hasHoverEffect
                        secondaryView={
                            <Checkbox
                                isChecked={scratchProduct?.allowInventory}
                                onChange={(e) => {
                                    setScratchProduct((p) => p?.clone({ allow_inventory: e }))
                                }}
                            />
                        }
                    />
                </WorkflowPage.Card>
            </div>
        </WorkflowPage>
    )
}
